import config from "next/config";
import type { Task } from "redux-saga";
import type { User } from "src/types";
import { isRecordingsError, RecordingsErrorResponse } from "src/types";

import { createDefaultPage } from "@/content-hub/editor/utils";
import { identifyUser } from "@/features/analytics";
import { getUniqueRecordingIdsFromPage } from "@/features/recordings/helpers";
import {
  fetchAuthenticated,
  fetchContentApi,
  uploadMedia,
} from "@/redux/fetch-saga";
import type { RootState } from "@/redux/store";
import {
  all,
  call,
  cancel,
  debounce,
  delay,
  fork,
  join,
  NarrowStoreEvent,
  put,
  select,
  take,
  takeLatest,
} from "@/redux/typed-saga-effects";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";
import logger from "@/utils/logger";
import { redirectToDashboard } from "@/utils/redirects";

import { getUniqueEventExternalIdsFromPage } from "../event-summaries/helpers";
import type { ContentHubEventSummaryResponse } from "../event-summaries/types";
import type { EventActionInput, ViewEventInput } from "../events/schema";
import type { VideoEvent } from "../events/types";
import { mapLinkedEventOptionToContentHubEventSummaryRaw } from "../linked-events/helpers";
import type { LinkedEventOptionsResponse } from "../linked-events/types";
import type {
  ColorPreset,
  MediaItem,
  MediaItemWithUploadUrl,
  Page,
} from "../page/domain";
import { transformColorPreset } from "../theme/transform-color-preset";
import type {
  Config,
  RecordingListRawResponse,
  UploadTransaction,
} from "../types";
import { generateSHA256Checksum, getPageUrl } from "../utils";
import { showToast } from "../utils/toast";
import {
  getDefaultAuthToken,
  getEventSummariesAuthToken,
  getOrganizationExternalId,
  getPage,
  getSpaceExternalId,
  getSpacesEnabled,
} from "./selectors";

const { publicRuntimeConfig } = config();
const get = endpoints();

export function* initializeEditor({
  payload: { organizationExternalId, pageSlug, spaceExternalId, spacesEnabled },
}: NarrowStoreEvent<"[page editor] initialize editor">) {
  const [scopedAuthResult, authResult] = yield* all([
    call(
      authenticateScoped,
      organizationExternalId,
      spaceExternalId,
      spacesEnabled
    ),
    call(authenticate),
  ]);

  if (!scopedAuthResult || !authResult) {
    yield* put({ type: "[page editor] user authentication failed" });
    yield* call(redirectToDashboard);
    return;
  }

  yield* put({
    type: "[page editor] user authentication successful",
    payload: {
      contentApiAuthToken: scopedAuthResult.token,
      contentApiEndpoint: scopedAuthResult.endpoints.content_api,
      organizationExternalId,
      defaultAuthToken: authResult?.token,
    },
  });

  //recordings reducer and identifyAnalyticsUser uses authenticatedFetch for fetching recordings
  //so we need to prepare headers for authenticatedFetch with the auth token
  authenticatedFetch.prepareHeaders({ token: authResult?.token });

  yield* takeLatest("[analytics] identify user", identifyAnalyticsUser);
  const colorPresetsTask = yield* fork(
    fetchColorPresets,
    organizationExternalId
  );

  yield* fork(watchUploadImage);

  yield* fork(fetchConfig, organizationExternalId);

  if (pageSlug) {
    yield* call(fetchPage, organizationExternalId, pageSlug);
  } else {
    const presets = yield* join(colorPresetsTask);
    yield* put({
      type: "[page editor] page created",
      payload: {
        page: createDefaultPage(
          presets.find((preset) => preset.isDefault) || presets[0]
        ),
      },
    });
  }

  yield* debounce(300, "[page editor] publish page clicked", savePage);

  yield* takeLatest(
    ["[page editor] save page", "[page editor] save page slug clicked"],
    savePage
  );
  yield* takeLatest("[page editor] page slug updated", validatePageSlug);
  yield* takeLatest("[page editor] fetch recordings", fetchRecordingsByIds);
  yield* takeLatest(
    "[page editor] fetch event summaries",
    fetchEventSummariesByIds
  );
}

type AuthenticationResult = {
  token: string;
  endpoints: { content_api: string };
};

// Fails closed: a spaces-enabled editor must not authenticate until a
// spaceExternalId is available, so a missing/deep-linked query param can't
// mint an unscoped Content API token. Mirrors the OS-14 monolith contract,
// which itself rejects a missing space_external_id (422) while the flag is
// on. When spacesEnabled is false, space_external_id is omitted entirely
// even if the URL happens to carry one (legacy org-wide behavior).
export async function authenticateScoped(
  organizationExternalId: string,
  spaceExternalId?: string,
  spacesEnabled?: boolean
): Promise<AuthenticationResult | null> {
  if (spacesEnabled && !spaceExternalId) return null;

  try {
    const body: Record<string, string> = {
      scope: "content_api",
      organization_external_id: organizationExternalId,
    };

    if (spacesEnabled && spaceExternalId) {
      body.space_external_id = spaceExternalId;
    }

    const response = await fetch(
      `${publicRuntimeConfig.HOPIN_ROOT}/users/scoped_sso`,
      {
        method: "POST",
        body: JSON.stringify(body),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) return null;

    return (await response.json()) as Promise<AuthenticationResult>;
  } catch (error) {
    logger.error(error, { details: "Failed to authenticate to content hub" });
    return null;
  }
}

async function authenticate(): Promise<AuthenticationResult | null> {
  try {
    const response = await fetch(
      `${publicRuntimeConfig.HOPIN_ROOT}/users/sso`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) return null;

    return (await response.json()) as Promise<AuthenticationResult>;
  } catch (error) {
    logger.error(error, { details: "Failed to authenticate user" });
    return null;
  }
}

function* savePage() {
  yield* delay(300);
  yield* put({
    type: "[page editor] save page started",
  });

  const { organizationExternalId, page } = yield* select((state: RootState) => {
    return {
      organizationExternalId: state.contentHub.organizationExternalId,
      page: state.contentHub.page,
    };
  });

  try {
    const pageResponse = yield* page.externalId
      ? call(updatePage, organizationExternalId, page.externalId, page)
      : call(createPage, organizationExternalId, page);

    yield* put({
      type: "[page editor] save page successful",
      payload: {
        page: pageResponse,
      },
    });
    showToast("content-hub.save-page-successful", "success");
  } catch (error: any) {
    const errors = error?.errors || {};

    yield* put({
      type: "[page editor] save page failed",
      payload: {
        errors,
      },
    });

    if (errors["published"]) {
      showToast("content-hub.publish-page-failed", "error");
      return;
    }

    showToast("content-hub.save-page-failed", "error");
  }
}

function* createPage(organizationExternalId: string, page: Page) {
  const response = yield* call(() =>
    fetchContentApi<Page>(`/${organizationExternalId}/pages`, "POST", {
      ...page,
    })
  );

  window.history.replaceState(
    undefined,
    "",
    window.location.pathname.replace("create", `${response.externalId}/edit`)
  );

  return response;
}

function* updatePage(
  organizationExternalId: string,
  pageId: string,
  page: Page
) {
  return yield* call(() =>
    fetchContentApi<Page>(`/${organizationExternalId}/pages/${pageId}`, "PUT", {
      ...page,
    })
  );
}

function* fetchPage(organizationExternalId: string, pageSlug: string) {
  try {
    const page = yield* call(() =>
      fetchContentApi<Page>(
        `/${organizationExternalId}/pages/${pageSlug}`,
        "GET"
      )
    );

    yield* put({
      type: "[page editor] page load successful",
      payload: {
        page: page,
      },
    });
    const recordingIds = getUniqueRecordingIdsFromPage(page);
    yield* call(fetchRecordingsByIds, { payload: { recordingIds } });

    const eventExternalIds = getUniqueEventExternalIdsFromPage(page);
    yield* fetchEventSummariesByIds({
      type: "[page editor] fetch event summaries",
      payload: { eventExternalIds, pageSlug },
    });
  } catch (error) {
    logger.error(error, { details: "Failed to fetch page" });
    yield* call(redirectToDashboard);
  }
}

function* fetchConfig(organizationExternalId: string) {
  try {
    const config = yield* call(() =>
      fetchContentApi<Config>(`/${organizationExternalId}/config`, "GET")
    );

    yield* put({
      type: "[page editor] fetch config successful",
      payload: {
        config: config,
      },
    });
  } catch (error) {
    logger.error(error, { details: "Failed to fetch config" });
  }
}

function* fetchColorPresets(organizationExternalId: string) {
  yield* put({
    type: "[page editor] fetch color presets started",
  });

  const authToken = yield* select(getDefaultAuthToken);

  try {
    const response = yield* call(() =>
      fetchAuthenticated<{ presets: ColorPreset[] }>(
        `${publicRuntimeConfig.HOPIN_ROOT}/api/v1/organisers/${organizationExternalId}/branding/presets`,
        "GET",
        authToken
      )
    );

    const transformedPresets = response.presets.map(transformColorPreset);

    yield* put({
      type: "[page editor] fetch color presets successful",
      payload: {
        presets: transformedPresets,
      },
    });

    return transformedPresets;
  } catch (error) {
    logger.error(error, { details: "Failed to fetch color presets" });
    yield* put({
      type: "[page editor] fetch color presets failed",
    });
  }
}

function* watchUploadImage() {
  const tasks: Record<string, Task> = {};

  try {
    while (true) {
      const event = (yield* take(
        "[page editor] image upload started"
      )) as NarrowStoreEvent<"[page editor] image upload started">;
      const { uploadTransactionId } = event.payload;
      const currentTask = tasks[uploadTransactionId];

      if (currentTask && currentTask.isRunning()) {
        yield* cancel(currentTask);
      }

      tasks[uploadTransactionId] = yield* fork(uploadImage, event.payload);
    }
  } finally {
    yield* cancel(Object.values(tasks));
  }
}

function* uploadImage({
  uploadTransactionId,
  file,
  onSuccess,
}: UploadTransaction & { onSuccess?: (mediaItem: MediaItem) => void }) {
  try {
    const mediaItemWithUrl = yield* call(createMediaItem, file);

    yield* put({
      type: "[page editor] image uploading",
      payload: {
        uploadTransactionId,
        mediaItemWithUrl,
      },
    });

    yield* call(uploadMedia, mediaItemWithUrl.uploadUrl, file);

    onSuccess?.(mediaItemWithUrl);

    yield* put({
      type: "[page editor] image upload successful",
      payload: {
        uploadTransactionId,
      },
    });
  } catch (error) {
    switch (true) {
      case error?.errors?.contentType?.code === "invalid_string":
        showToast("content-hub.image-upload-invalid-file-type", "error");
        break;

      case error?.errors?.contentLength?.code === "too_big":
        showToast("content-hub.image-upload-too-big", "error");
        break;

      case error?.errors?.contentLength?.code === "too_small":
      case error?.errors?.checksum?.code !== undefined:
        showToast("content-hub.image-invalid", "error");
        break;

      default:
        showToast("content-hub.image-upload-failed", "error");
    }

    yield* put({
      type: "[page editor] image upload failed",
      payload: {
        uploadTransactionId,
      },
    });
    logger.error(error, { details: "Failed to upload image" });
  }
}

function* createMediaItem(file: File) {
  const organizationExternalId = yield* select(getOrganizationExternalId);

  const checksum = yield* call(generateSHA256Checksum, file);

  const response = yield* call(() =>
    fetchContentApi<MediaItemWithUploadUrl>(
      `/${organizationExternalId}/media`,
      "POST",
      {
        filename: file.name,
        contentType: file.type,
        contentLength: file.size,
        checksum,
      }
    )
  );
  return response;
}

export function* initializePage({
  payload: { organizationExternalId, pageSlug, page, isBot },
}: NarrowStoreEvent<"[content page] initialize page">) {
  if (!isBot) yield* takeLatest("[content page] submit event", sendEvent);
  yield* takeLatest("[content page] submit video event", sendVideoEvent);
  yield* takeLatest(
    ["[content page] fetch recordings", "[content page] fetch all recordings"],
    fetchContentPageRecordings
  );
  yield* takeLatest(
    [
      "[content page] fetch event summaries",
      "[content page] fetch all event summaries",
    ],
    fetchContentPageEventSummaries
  );

  if (page) {
    yield* put({
      type: "[content page] page load successful",
      payload: {
        page,
      },
    });
    yield* call(validateSession);

    yield* put({
      type: "[content page] submit event",
      payload: {
        event: getViewEvent(),
      },
    });
    yield* put({
      type: "[content page] fetch all recordings",
    });
    yield* put({
      type: "[content page] fetch all event summaries",
    });
  } else {
    yield* call(fetchPublishedPage, organizationExternalId, pageSlug);
  }
}

/**
 * @todo this should do one thing.
 * Orchestration of session validation, page view tracking and fetching of recordings should be done in separate effects.
 * Instead of "putting" events, we should be "calling" or "forking" effects, this will make the orchestration more explicit and flexible.
 * Returning the page from the effect will make it easier to orchestrate the aforementioned effects.
 */
function* fetchPublishedPage(organizationExternalId: string, pageSlug: string) {
  try {
    const response = yield* call(() =>
      fetch(getPageUrl(organizationExternalId, pageSlug), {
        method: "GET",
      })
    );

    if (!response.ok) {
      if (response.status === 404) {
        yield* put({
          type: "[content page] page not found",
        });
        return;
      }

      throw new Error("Page Load failed");
    }

    const page = yield* call(() => response.json() as Promise<Page>);

    if (!page) {
      yield* put({
        type: "[content page] page not found",
      });
      return;
    }

    yield* put({
      type: "[content page] page load successful",
      payload: {
        page,
      },
    });

    //validate session
    yield* call(validateSession);

    //submit view event
    yield* put({
      type: "[content page] submit event",
      payload: {
        event: getViewEvent(),
      },
    });

    //fetch all recordings on page load
    yield* put({
      type: "[content page] fetch all recordings",
    });

    yield* put({
      type: "[content page] fetch all event summaries",
    });
  } catch (error) {
    yield* put({
      type: "[content page] page not found",
    });
  }
}

function* validatePageSlug({
  payload: { pageSlug },
}: NarrowStoreEvent<"[page editor] page slug updated">) {
  try {
    yield* delay(500);
    yield* put({
      type: "[page editor] page slug validation started",
    });
    const organizationExternalId = yield* select(getOrganizationExternalId);
    const page = yield* select(getPage);

    const [response] = yield all([
      call(() =>
        fetchContentApi<{ slugAvailable: boolean }>(
          `/${organizationExternalId}/pages/validate-slug`,
          "POST",
          {
            slug: pageSlug,
            externalId: page.externalId,
          }
        )
      ),
      delay(300),
    ]);

    if (response.slugAvailable)
      yield* put({
        type: "[page editor] page slug validation successful",
        payload: {
          pageSlug,
        },
      });
    else
      yield* put({
        type: "[page editor] page slug validation failed",
        payload: {
          error: "SLUG_ALREADY_IN_USE",
        },
      });
  } catch (error) {
    yield* put({
      type: "[page editor] page slug validation failed",
      payload: {
        error: "FAILED_TO_VALIDATE_SLUG",
      },
    });
  }
}

function getSessionCookie() {
  const cookie = document.cookie
    .split(";")
    .find((cookie) => cookie.startsWith("__cp_sn="));

  return cookie?.replace("__cp_sn=", "");
}

function* validateSession() {
  try {
    const sessionToken = yield* call(getSessionCookie);

    const response = yield* call(() =>
      fetch("/canvas/api/content/validate-session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
      })
    );

    if (!response.ok) throw new Error("FAILED_TO_VALIDATE_SESSION");

    const { content_gate_completed: contentGateCompleted } = yield* call(() =>
      response.json()
    );

    yield* put({
      type: "[content page] session validation successful",
      payload: {
        contentGateCompleted,
      },
    });
  } catch (err) {
    yield* put({
      type: "[content page] session validation failed",
      payload: {
        error: "FAILED_TO_VALIDATE_SESSION",
      },
    });
  }
}

/**
 * @todo send event should do one thing.
 * Orchestration session and fetching of recordings should be done in separate effects.
 */
function* sendEvent({
  payload: { event },
}: NarrowStoreEvent<"[content page] submit event">) {
  try {
    const page = yield* select(getPage);
    const sessionToken = yield* call(getSessionCookie);

    const eventBody: EventActionInput = {
      ...event,
      pageId: page.externalId,
      pageUrl: `${window.location.href}`,
      pageTitle: page.metadata.title,
    };

    const response = yield* call(() =>
      fetch("/canvas/api/content/events", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventBody),
      })
    );

    if (!response.ok) throw new Error("FAILED_TO_SEND_EVENT");

    const session = yield* call(() => response.json());
    const contentGateCompleted = session.contentGateCompleted;

    yield* put({
      type: "[content page] submit event successful",
      payload: {
        contentGateCompleted,
      },
    });

    //refetch all recordings if contact event is sent and content gate is completed
    if (event.action === "contact" && contentGateCompleted) {
      yield* put({
        type: "[content page] fetch all recordings",
      });
    }
  } catch (error) {
    yield* put({
      type: "[content page] submit event failed",
      payload: {
        errors: { event: "FAILED_TO_SEND_EVENT" },
      },
    });
  }
}

export function* sendVideoEvent({
  payload: { event },
}: NarrowStoreEvent<"[content page] submit video event">) {
  try {
    const page = yield* select(getPage);
    const organizationExternalId = yield* select(getOrganizationExternalId);
    const spaceExternalId = yield* select(getSpaceExternalId);
    const sessionToken = yield* call(getSessionCookie);

    const eventBody: VideoEvent = {
      ...event,
      page_id: page.externalId,
      org_id: organizationExternalId,
      session_id: sessionToken,
      space_external_id: spaceExternalId ?? undefined,
    };

    const response = yield* call(() =>
      fetch(`${publicRuntimeConfig.HOPIN_ANALYTICS_HOST}/player`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventBody),
      })
    );
    if (!response.ok) throw new Error("FAILED_TO_SEND_VIDEO_EVENT");

    yield* put({
      type: "[content page] submit video event successful",
    });
  } catch (error) {
    yield* put({
      type: "[content page] submit video event failed",
      payload: {
        errors: { event: "FAILED_TO_SEND_VIDEO_EVENT" },
      },
    });
  }
}

function* fetchUserDetails() {
  try {
    const response = yield* call(() =>
      authenticatedFetch.fetchJSON(get("HOPIN_USER"), {
        credentials: "include",
      })
    );
    if (isError(response)) {
      logger.error(response, { details: "Failed to fetch user details" });
      return null;
    }

    return response as User;
  } catch (error) {
    logger.error(error, { details: "Failed to fetch user details" });
    return null;
  }
}

function* identifyAnalyticsUser() {
  try {
    const user = yield* call(fetchUserDetails);

    if (user?.external_id) {
      yield* call(identifyUser, user.external_id);
    }
  } catch (error) {
    logger.error(error, { details: "Failed to identify user in analytics" });
    yield* put({
      type: "[analytics] identify user failed",
    });
  }
}

function* fetchRecordingsByIds({
  payload: { recordingIds },
}: NarrowStoreEvent<"[page editor] fetch recordings">) {
  if (!recordingIds.length) return [];

  const organizationExternalId = yield* select(getOrganizationExternalId);
  const spaceExternalId = yield* select(getSpaceExternalId);
  const spacesEnabled = yield* select(getSpacesEnabled);

  try {
    const response = yield* call(() =>
      authenticatedFetch.fetchJSON<
        RecordingListRawResponse,
        RecordingsErrorResponse
      >(
        get(`GET_RECORDINGS`)(
          organizationExternalId,
          recordingIds,
          spacesEnabled ? spaceExternalId ?? undefined : undefined
        )
      )
    );

    if (isRecordingsError(response)) {
      yield* put({
        type: "[page editor] fetch recordings failed",
        payload: {
          errors: { event: "FAILED_TO_FETCH_RECORDINGS" },
        },
      });
      return response.error;
    } else {
      yield* put({
        type: "[page editor] fetch recordings successful",
        payload: {
          recordings: response.data,
        },
      });
      return response.data;
    }
  } catch (error) {
    yield* put({
      type: "[page editor] fetch recordings failed",
      payload: {
        errors: { event: "FAILED_TO_FETCH_RECORDINGS" },
      },
    });
  }
}

function* fetchEventSummariesByIds({
  payload: { eventExternalIds, pageSlug },
}: NarrowStoreEvent<"[page editor] fetch event summaries">) {
  if (!eventExternalIds.length) return [];

  const organizationExternalId = yield* select(getOrganizationExternalId);
  const authToken = yield* select(getEventSummariesAuthToken(pageSlug));

  try {
    const queryParams = new URLSearchParams();
    queryParams.append("organization_id", organizationExternalId);
    queryParams.append("eventExternalIds", eventExternalIds.join(","));
    if (pageSlug) queryParams.append("pageSlug", pageSlug);

    const response = yield* call(() =>
      fetch(
        `/canvas/api/content/${
          pageSlug ? "events" : "event-options"
        }?${queryParams}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      )
    );

    if (!response.ok) {
      yield* put({
        type: "[page editor] fetch event summaries failed",
        payload: {
          errors: { event: "FAILED_TO_FETCH_EVENT_SUMMARIES" },
        },
      });
      return;
    }

    if (pageSlug) {
      const eventSummariesResponse = (yield* call(() =>
        response.json()
      )) as ContentHubEventSummaryResponse;

      yield* put({
        type: "[page editor] fetch event summaries successful",
        payload: {
          eventSummaries: eventSummariesResponse.data,
          unavailableEventExternalIds:
            eventSummariesResponse.unavailable_event_ids,
        },
      });

      return eventSummariesResponse.data;
    }

    const linkedEventOptionsResponse = (yield* call(() =>
      response.json()
    )) as LinkedEventOptionsResponse;
    const returnedEventExternalIds = new Set(
      linkedEventOptionsResponse.data.map((eventOption) => eventOption.id)
    );

    yield* put({
      type: "[page editor] fetch event summaries successful",
      payload: {
        eventSummaries: linkedEventOptionsResponse.data.map((eventOption) =>
          mapLinkedEventOptionToContentHubEventSummaryRaw(eventOption)
        ),
        unavailableEventExternalIds: eventExternalIds.filter(
          (eventExternalId) => !returnedEventExternalIds.has(eventExternalId)
        ),
      },
    });

    return linkedEventOptionsResponse.data;
  } catch (error) {
    yield* put({
      type: "[page editor] fetch event summaries failed",
      payload: {
        errors: { event: "FAILED_TO_FETCH_EVENT_SUMMARIES" },
      },
    });
  }
}

function* fetchContentPageRecordings(
  eventPayload?: NarrowStoreEvent<
    "[content page] fetch recordings" | "[content page] fetch all recordings"
  >
) {
  const { recordingIds, callbacks } =
    (eventPayload as NarrowStoreEvent<"[content page] fetch recordings">)
      ?.payload || {};
  const page = yield* select(getPage);
  const sessionToken = yield* call(getSessionCookie);
  const pageSlug = page.slug;
  const hasRecordingIds = recordingIds?.length;

  const response = yield* call(() =>
    fetch(
      `/canvas/api/content/recordings?pageSlug=${pageSlug}${
        hasRecordingIds ? `&recordingIds=${recordingIds.join(",")}` : ""
      }`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      }
    )
  );
  if (!response.ok) {
    yield* put({
      type: "[content page] fetch recordings failed",
      payload: {
        errors: { event: "FAILED_TO_FETCH_RECORDINGS" },
      },
    });
    return;
  }
  const recordingsResponse = yield* call(() => response.json());
  if (isRecordingsError(recordingsResponse)) {
    yield* put({
      type: "[page editor] fetch recordings failed",
      payload: {
        errors: { event: "FAILED_TO_FETCH_RECORDINGS" },
      },
    });
  } else {
    yield* put({
      type: "[page editor] fetch recordings successful",
      payload: {
        recordings: recordingsResponse.data,
      },
    });
    callbacks?.onSuccess?.(recordingsResponse.data);
  }
}

function* fetchContentPageEventSummaries(
  eventPayload?: NarrowStoreEvent<
    | "[content page] fetch event summaries"
    | "[content page] fetch all event summaries"
  >
) {
  const { eventExternalIds } =
    (eventPayload as NarrowStoreEvent<"[content page] fetch event summaries">)
      ?.payload || {};
  const page = yield* select(getPage);

  if (!page) return;

  const requestedEventExternalIds =
    eventExternalIds ?? getUniqueEventExternalIdsFromPage(page);

  if (!requestedEventExternalIds.length) return;

  const sessionToken = yield* call(getSessionCookie);
  const pageSlug = page.slug;

  try {
    const queryParams = new URLSearchParams();
    queryParams.append("pageSlug", pageSlug);
    queryParams.append("eventExternalIds", requestedEventExternalIds.join(","));

    const response = yield* call(() =>
      fetch(`/canvas/api/content/events?${queryParams}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      })
    );

    if (!response.ok) {
      yield* put({
        type: "[content page] fetch event summaries failed",
        payload: {
          errors: { event: "FAILED_TO_FETCH_EVENT_SUMMARIES" },
        },
      });
      return;
    }

    const eventSummariesResponse = (yield* call(() =>
      response.json()
    )) as ContentHubEventSummaryResponse;

    yield* put({
      type: "[content page] fetch event summaries successful",
      payload: {
        eventSummaries: eventSummariesResponse.data,
        unavailableEventExternalIds:
          eventSummariesResponse.unavailable_event_ids,
      },
    });
  } catch (error) {
    yield* put({
      type: "[content page] fetch event summaries failed",
      payload: {
        errors: { event: "FAILED_TO_FETCH_EVENT_SUMMARIES" },
      },
    });
  }
}

function getViewEvent(): Omit<
  ViewEventInput,
  "pageId" | "pageUrl" | "pageTitle"
> {
  const urlParams = new URLSearchParams(window.location.search);
  const source = urlParams.get("utm_source");
  const medium = urlParams.get("utm_medium");
  const campaign = urlParams.get("utm_campaign");
  const content = urlParams.get("utm_content");
  const term = urlParams.get("utm_term");

  if (!source) return { action: "view" };

  return {
    action: "view",
    utm: {
      source,
      ...(medium && { medium }),
      ...(campaign && { campaign }),
      ...(content && { content }),
      ...(term && { term }),
    },
  };
}
